import PropTypes from 'prop-types';
import {
  FormControlLabel, Radio, RadioGroup, Tooltip, makeStyles,
} from '@material-ui/core';
import {
  useEffect, useState, useCallback, memo,
  useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAnalytics } from 'use-analytics';
import { useRouter } from 'next/router';
import _ from 'lodash';
import { CreditCardCodes, SearchStatus, UserRolePermissions } from '../../../../constants';
import withSpinner from '../../../../HOCs/withSpinner';
import {
  deleteCardRequest, getSavedCardsRequest, setSelectedCreditCard, setDefaultCardRequest,
} from '../../../../store/slices/customer';
import {
  changeTrip, getCustomerIdToSaveCard, selectedCreditCard,
} from '../../../../store/slices/trip';
import useFocusOnError from '../../../../hooks/useFocusOnError';
import ErrorTooltip from '../../../ErrorTooltip';
import Button from '../../../Button';
import ConfirmModal from '../../../ConfirmModal';
import { checkSitePermission } from '../../../../store/slices/user';

const useStyles = makeStyles(() => ({
  label: {
    fontSize: 'inherit',
  },
}));
const useStylesRadioGroup = makeStyles(() => ({
  root: {
    '& .MuiFormControlLabel-label': {
      fontFamily: 'var(--fontFamilyBody)',
    },
  },
}));

const getCardNameByCardCode = (cardCode) => {
  const mapped = _.map(CreditCardCodes, (key, val) => (
    cardCode === key ? val : null));
  return _.find(mapped, (item) => item !== null) || cardCode;
};

const SavedCards = ({ tripId, acceptedCards }) => {
  const { track } = useAnalytics();
  const router = useRouter();
  const dispatch = useDispatch();
  const [cardToDelete, setCardToDelete] = useState();
  const [defaultCard, setDefaultCard] = useState();
  const trip = useSelector((state) => state.trip.selectedTrips[tripId] || {});
  const {
    getSavedCardsStatuses, savedCardsByCustomerId, deleteCardStatus,
    deleteCardError, setDefaultCardStatus, defaultCardError,
  } = useSelector((store) => store.customer);

  const {
    isSavedCardValidated, passengers,
    similarPassengersValidation, isAgencyPlanningFeesValid,
  } = useSelector((store) => store.trip);
  const doShowPaymentCheckboxes = router.query.doShowPaymentCheckboxes === 'true'
    && trip.doSupportsCardUpsertOperations;
  const canSetDefaultCard = useSelector((state) => (trip?.doSupportsCardUpsertOperations
    && (doShowPaymentCheckboxes
      || checkSitePermission(UserRolePermissions.canSetDefaultPaymentMethod)(state))));
  const canDeletePaymentMethod = useSelector((state) => (trip?.doSupportsCardUpsertOperations
    && (doShowPaymentCheckboxes
      || checkSitePermission(UserRolePermissions.canDeletePaymentMethod)(state))));
  const { useCardFromAgencyProfile } = useSelector((store) => store.user.travelAgency || {});
  const selectedCard = useSelector(selectedCreditCard);
  const customerId = useSelector(getCustomerIdToSaveCard);
  const classes = useStyles();
  const classesRadioGroup = useStylesRadioGroup();

  const searchStatus = getSavedCardsStatuses
    && getSavedCardsStatuses[customerId];
  const savedCards = savedCardsByCustomerId && savedCardsByCustomerId[customerId];

  const isErrorAppears = useMemo(() => (!isSavedCardValidated
    && !(Object
      .values(passengers)
      .some((item) => Object
        .values(item.formValidations)
        .some((validationItem) => validationItem))
      || Boolean(similarPassengersValidation)
      || Boolean(isAgencyPlanningFeesValid))),
  [isAgencyPlanningFeesValid, passengers, isSavedCardValidated, similarPassengersValidation]);
  const focusedOnErrorRef = useFocusOnError(isErrorAppears);

  const handleChangeSelectedCard = (event) => {
    const card = JSON.parse(event.target.value);
    dispatch(setSelectedCreditCard({
      customerId, selectedCard: card,
    }));
    dispatch(changeTrip({ field: 'isSavedCardValidated', value: true }));
    track('handleChangeSavedSelectedCard', { selectedCard: card && card.id });
  };

  const handleDeleteSelectedCard = useCallback(() => {
    dispatch(deleteCardRequest({
      card: cardToDelete,
      withAddressOnly: trip.isCardAddressRequired,
      customerId,
      doShowPaymentCheckboxes,
    }));
    setCardToDelete(undefined);
  }, [customerId, dispatch, cardToDelete, trip.isCardAddressRequired, doShowPaymentCheckboxes]);

  const handleSetDefaultCreditCard = useCallback(() => {
    dispatch(setDefaultCardRequest({
      card: defaultCard,
      withAddressOnly: trip.isCardAddressRequired,
      customerId,
      doShowPaymentCheckboxes,
    }));
    setDefaultCard(undefined);
  }, [customerId, dispatch, defaultCard, trip.isCardAddressRequired, doShowPaymentCheckboxes]);

  useEffect(() => {
    if ((useCardFromAgencyProfile || (customerId && customerId !== ''))
      && (!savedCards || !savedCards.length)
      && searchStatus !== SearchStatus.SEARCHING && searchStatus !== SearchStatus.DONE) {
      dispatch(getSavedCardsRequest({
        customerId,
        withAddressOnly: trip.isCardAddressRequired,
      }));
    }
  }, [customerId, dispatch, savedCards, searchStatus, trip.isCardAddressRequired,
    useCardFromAgencyProfile]);

  const savedCardErrorMessage = useMemo(() => {
    const savedCardErrors = [];
    if (savedCards?.length > 0 && savedCards.every((card) => card.isExpired)) {
      const message = savedCards.length === 1
        ? 'Your saved card has expired. To complete your booking, please add a new payment method.'
        : 'All your saved cards have expired. To complete your booking, please add a new payment method.';
      savedCardErrors.push(message);
    }

    const cardCode = selectedCard?.cardCode;
    if (cardCode && acceptedCards && Object.values(acceptedCards).length > 0
    && !acceptedCards?.[cardCode]) {
      savedCardErrors.push(`${getCardNameByCardCode(cardCode)} card isn't accepted. Please use: ${Object.values(acceptedCards || {}).join(', ')}`);
    }
    return savedCardErrors.length > 0
      ? savedCardErrors.join(' ')
      : 'Please select a payment method';
  }, [acceptedCards, savedCards, selectedCard]);

  useEffect(() => {
    if (focusedOnErrorRef?.current) {
      focusedOnErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isSavedCardValidated, focusedOnErrorRef]);

  if (trip && trip.isCardSecurityCodeRequired) {
    return (
      <div className="my-2 italic">The saved cards feature is not available when a security code is required. That feature will be available soon!</div>
    );
  }

  return (customerId && customerId !== '') || useCardFromAgencyProfile ? withSpinner(() => (
    savedCards && savedCards.length > 0
      ? (
        <>
          <div className="mt-2 font-bold">Saved Cards:</div>
          <div ref={focusedOnErrorRef}>
            <ErrorTooltip
              title={isSavedCardValidated === false ? savedCardErrorMessage : ''}
              open={isSavedCardValidated === false}
              arrow
              PopperProps={{ disablePortal: true }}
            >
              <RadioGroup
                aria-label="savedCards"
                name="savedCard"
                value={!selectedCard ? '' : JSON.stringify(selectedCard)}
                onChange={handleChangeSelectedCard}
                classes={classesRadioGroup}
              >
                {savedCards.map((card) => (
                  <div key={`${card.cardCode}${card.last4digits}`}>
                    <FormControlLabel
                      value={JSON.stringify(card)}
                      control={<Radio disabled={card.isExpired} />}
                      classes={classes}
                      label={(
                        <>
                          <span>
                            {getCardNameByCardCode(card.cardCode)}
                            {' '}
                            card ending with
                            {' '}
                            {card.last4digits}
                            {' '}
                            -
                            {' '}
                            {card.name}
                            {' '}
                            {(card.isDefault && useCardFromAgencyProfile ? (
                              <Tooltip title="Default credit card for payment by customers" placement="top">
                                <span className="font-bold">(default)</span>
                              </Tooltip>
                            ) : <></>)}
                            {card.isExpired && (
                              <span className="text-md text-red-600">(Expired)</span>
                            )}
                            {canDeletePaymentMethod ? (
                              <Button
                                variant={Button.buttonVariants.LINK}
                                className="focus:outline-none mr-2 underline"
                                size={Button.buttonSizes.XS}
                                onClick={() => setCardToDelete(card)}
                                doShowLoadingState={!!(cardToDelete && cardToDelete.id === card.id)
                              || !!(deleteCardStatus
                                && deleteCardStatus[card.id] === SearchStatus.SEARCHING)}
                              >
                                Delete
                              </Button>
                            ) : <></>}

                            {canSetDefaultCard && !card.isDefault ? (
                              <Button
                                variant={Button.buttonVariants.LINK}
                                className="focus:outline-none underline"
                                size={Button.buttonSizes.XS}
                                onClick={() => setDefaultCard(card)}
                                doShowLoadingState={!!(defaultCard && defaultCard.id === card.id)
                              || !!(setDefaultCardStatus
                                && setDefaultCardStatus[card.id] === SearchStatus.SEARCHING)}
                              >
                                Set as default
                              </Button>
                            ) : <></>}
                          </span>
                          {card.info ? (
                            <div className="text-xs text-gray-600">
                              {card.info}
                            </div>
                          ) : <></>}
                        </>
                      )}
                    />
                  </div>
                ))}
              </RadioGroup>
            </ErrorTooltip>
            {deleteCardError && <div className="text-red-500 mt-1 ml-2 text-sm font-bold">{deleteCardError}</div>}
            {defaultCardError && <div className="text-red-500 mt-1 ml-2 text-sm font-bold">{defaultCardError}</div>}
            <div className="mt-2" />
            {cardToDelete && (
            <ConfirmModal
              message={(
                <span>
                  Are you sure to remove
                  <b>
                    {' '}
                    {getCardNameByCardCode(cardToDelete.cardCode)}
                    {' '}
                    card ending with
                    {' '}
                    {cardToDelete.last4digits}
                    {' '}
                    -
                    {' '}
                    {cardToDelete.name}
                  </b>
                </span>
                  )}
              onCancel={() => setCardToDelete(undefined)}
              onConfirm={handleDeleteSelectedCard}
              event="RemoveSavedCard"
              eventDetail={`Are you sure to remove ${getCardNameByCardCode(cardToDelete.cardCode)} card ending with ${cardToDelete.last4digits}`}
            />
            )}
            {defaultCard && (
            <ConfirmModal
              message={(
                <span>
                  Are you sure to set default card to
                  <b>
                    {' '}
                    {getCardNameByCardCode(defaultCard.cardCode)}
                    {' '}
                    card ending with
                    {' '}
                    {defaultCard.last4digits}
                    {' '}
                    -
                    {' '}
                    {defaultCard.name}
                  </b>
                  ?
                </span>
                )}
              onCancel={() => setDefaultCard(undefined)}
              onConfirm={handleSetDefaultCreditCard}
              event="SetDefaultSavedCard"
              eventDetail={`Are you sure to set default card to ${getCardNameByCardCode(defaultCard.cardCode)} card ending with ${defaultCard.last4digits}`}
            />
            )}
          </div>
        </>
      ) : (
        <ErrorTooltip
          title="There are no saved cards. Use new card to continue checkout."
          open={isSavedCardValidated === false}
          arrow
          PopperProps={{ disablePortal: true }}
        >
          <div ref={focusedOnErrorRef} className="mt-2 mb-4 italic">Saved cards not found!</div>
        </ErrorTooltip>
      )
  ), searchStatus === SearchStatus.SEARCHING, 'Searching for saved cards...', 'left-inline', 'xs', 'mb-4') : <></>;
};

SavedCards.propTypes = {
  tripId: PropTypes.string.isRequired,
  acceptedCards: PropTypes.shape(),
};
SavedCards.defaultProps = {
  acceptedCards: {},
};
export default memo(SavedCards);
